// ----------------------------------------------------------------------------
// Plan (/admin/subscription). Tier cards from PRICING_TIERS with the same
// rules as before: Founders is hidden unless it's the current tier, Enterprise
// goes to Contact sales, downgrades confirm first (member limit + Song Studio
// retention), a church with a live Stripe subscription changes price in place
// and anyone else goes through Checkout. Handles the ?checkout= return.
// ----------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { useEntitlements } from '@admin/shared/contexts/EntitlementsContext';
import { isForbiddenError } from '@app/lib/apiErrors';
import {
  PRICING_TIERS,
  buildEnterpriseContactMailto,
  cancelSubscription,
  createCheckoutSession,
  getChurchMemberCount,
  getSubscription,
  openBillingPortal,
  upgradeSubscription,
  verifyCheckoutSession,
  type PricingTier,
  type Subscription,
} from '@app/api/subscription';
import { chordUploadsApi } from '@app/api/chords';
import { Banner, Button, EmptyState, PageHeader, Segmented, SkeletonRows, StatusPill, cx } from '@app/components/ui';
import { displayTierName, getTierFeatures } from '@app/utils/tierUtils';
import { redesignFlags } from '@app/featureFlags';
import { logger } from '@app/utils/logger';
import { CancelPlanModal, DowngradeModal } from './PlanModals';

type PaidTier = 'basic' | 'founders' | 'pro' | 'enterprise';
const RANK = { free: 0, basic: 1, founders: 2, pro: 3, enterprise: 4 } as const;
const errText = (err: any, fallback: string) => err?.response?.data?.error || err?.response?.data?.message || fallback;
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const Plan: React.FC = () => {
  const { user } = useAuth();
  const { refreshEntitlements } = useEntitlements();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const churchId = user?.churchId;
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [switching, setSwitching] = useState<PaidTier | null>(null);
  const [portal, setPortal] = useState(false);
  const [downgradeTo, setDowngradeTo] = useState<PaidTier | null>(null);
  const [chordCount, setChordCount] = useState(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const verifying = useRef(false);

  const load = async () => {
    if (!churchId) return;
    try {
      setLoading(true);
      setForbidden(false);
      setSub(await getSubscription(churchId));
    } catch (err) {
      if (isForbiddenError(err)) setForbidden(true);
      else {
        logger.error('Error loading subscription:', err);
        setError('Could not load your plan');
      }
    } finally {
      setLoading(false);
    }
  };

  // Stripe Checkout return.
  useEffect(() => {
    const status = params.get('checkout');
    const sessionId = params.get('session_id');
    if (status === 'success' && sessionId) {
      const key = `verify-session-${sessionId}`;
      if (verifying.current || sessionStorage.getItem(key)) return;
      verifying.current = true;
      sessionStorage.setItem(key, 'pending');
      setLoading(true);
      verifyCheckoutSession(sessionId)
        .then((s) => {
          setSub(s);
          setSuccess('Payment received. Your new plan is active.');
          void refreshEntitlements();
        })
        .catch((err) => {
          logger.error('Error verifying checkout session:', err);
          setSuccess('Payment received. Your plan may take a moment to update.');
          void load();
          void refreshEntitlements();
        })
        .finally(() => {
          setLoading(false);
          setParams({}, { replace: true });
          sessionStorage.removeItem(key);
        });
    } else if (status === 'success') {
      setSuccess('Payment received. Your plan may take a moment to update.');
      setParams({}, { replace: true });
    } else if (status === 'canceled') {
      setError('Checkout was canceled. You were not charged.');
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, setParams]);

  useEffect(() => {
    void load();
    if (churchId) getChurchMemberCount(churchId).then(setMemberCount).catch((err) => logger.error('Error loading member count:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId]);

  const current = sub?.tier || 'free';
  const isFounderPricing = sub?.isFounder === true;

  const contactSales = () => {
    window.location.href = buildEnterpriseContactMailto(churchId ?? undefined, sub?.tier);
  };

  const checkout = async (tier: PaidTier) => {
    if (!churchId) return;
    try {
      setSwitching(tier);
      setError(null);
      const res = await createCheckoutSession({ churchId, tier });
      window.location.href = res.checkoutUrl;
    } catch (err) {
      logger.error('Error switching tier:', err);
      setError(errText(err, 'Could not start checkout'));
      setSwitching(null);
    }
  };

  // A live Stripe subscription changes price in place (one subscription, prorated).
  // Checkout would create a second subscription. Only use Checkout when there's none.
  const changeTier = async (tier: PaidTier) => {
    if (!churchId) return;
    const live = !!sub?.stripeSubscriptionId && sub.status !== 'canceled';
    if (!live) return checkout(tier);
    try {
      setSwitching(tier);
      setError(null);
      await upgradeSubscription(churchId, tier);
      await load();
      await refreshEntitlements();
      setSuccess(`You're now on ${displayTierName(tier)}.`);
    } catch (err) {
      logger.error('Error switching tier:', err);
      setError(errText(err, 'Could not change your plan'));
    } finally {
      setSwitching(null);
    }
  };

  const choose = async (tier: PaidTier) => {
    if (!churchId) return;
    if (tier === 'enterprise') return contactSales();
    if (RANK[tier] < RANK[current as keyof typeof RANK]) {
      try {
        setChordCount((await chordUploadsApi.list(churchId)).length);
      } catch {
        setChordCount(0);
      }
      setDowngradeTo(tier);
      return;
    }
    await changeTier(tier);
  };

  const manageBilling = async () => {
    if (!churchId) return;
    setPortal(true);
    setError(null);
    try {
      window.location.href = await openBillingPortal(churchId);
    } catch (err) {
      logger.error('Error opening billing portal:', err);
      setError(errText(err, 'Could not open the billing portal'));
      setPortal(false);
    }
  };

  const cancel = async (immediate: boolean) => {
    if (!churchId) return;
    setCancelError(null);
    setCancelBusy(true);
    try {
      await cancelSubscription(churchId, immediate);
      setCancelOpen(false);
      await load();
      await refreshEntitlements();
    } catch (err) {
      logger.error('Error canceling subscription:', err);
      setCancelError(errText(err, 'Could not cancel your plan'));
    } finally {
      setCancelBusy(false);
    }
  };

  if (forbidden) {
    return (
      <div>
        <PageHeader title="Plan" />
        <EmptyState title="Only church admins can manage the plan" body="Ask an admin of this church to give you access." />
      </div>
    );
  }

  const tiers = PRICING_TIERS.filter((t) => t.tier !== 'founders' || t.tier === current);
  const live = sub && (sub.status === 'active' || sub.status === 'trialing');
  const validEnd = sub && new Date(sub.currentPeriodEnd).getFullYear() > 1970;
  const statusLine = !sub
    ? null
    : sub.status === 'trialing' && sub.trialEndsAt
      ? `Trial ends ${shortDate(sub.trialEndsAt)}`
      : sub.cancelAtPeriodEnd && validEnd
        ? `Ends ${shortDate(sub.currentPeriodEnd)}`
        : live && validEnd && current !== 'free'
          ? `Renews ${shortDate(sub.currentPeriodEnd)}`
          : sub.status === 'past_due'
            ? 'Payment overdue'
            : null;
  const comp = sub?.compStatus?.type;

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            You're on {displayTierName(current)}
            {comp === 'lifetime' ? (
              <StatusPill tone="amb">Lifetime supporter</StatusPill>
            ) : comp === 'founder' || isFounderPricing ? (
              <StatusPill tone="amb">Founder pricing</StatusPill>
            ) : null}
            {statusLine && <span className="text-t4">· {statusLine}</span>}
          </>
        }
        title="Choose a plan"
        actions={
          redesignFlags.yearlyBilling && (
            <Segmented
              label="Billing period"
              value={period}
              onChange={setPeriod}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly · save 20%' },
              ]}
            />
          )
        }
      />

      {success && (
        <Banner tone="ok" role="status" className="mb-3.5 flex items-center justify-between gap-3">
          <span>{success}</span>
          <button type="button" className="text-[13px] underline" onClick={() => setSuccess(null)}>
            Dismiss
          </button>
        </Banner>
      )}
      {error && (
        <Banner tone="acc" role="alert" className="mb-3.5 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button type="button" className="text-[13px] underline" onClick={() => setError(null)}>
            Dismiss
          </button>
        </Banner>
      )}

      {loading ? (
        <SkeletonRows rows={3} />
      ) : (
        <>
          <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(260px,100%),1fr))' }}>
            {tiers.map((t) => (
              <TierCard key={t.tier} tier={t} current={t.tier === current} currentTier={current} founder={isFounderPricing} switching={switching} onChoose={choose} />
            ))}
          </div>

          <div className="flex flex-wrap gap-2.5 mt-[22px] items-center">
            {sub && current !== 'free' && sub.stripeCustomerId && (
              <Button variant="ghost" loading={portal} onClick={manageBilling}>
                Update payment method
              </Button>
            )}
            <Button variant="link" onClick={() => navigate('/admin/billing')}>
              Billing history
            </Button>
            <span className="flex-1" />
            {sub && current !== 'free' && !sub.cancelAtPeriodEnd && (
              <Button
                variant="plain"
                className="!text-t3 hover:!text-acc"
                onClick={() => {
                  setCancelError(null);
                  setCancelOpen(true);
                }}
              >
                Cancel plan
              </Button>
            )}
          </div>
        </>
      )}

      {downgradeTo && (
        <DowngradeModal
          open
          onClose={() => {
            setDowngradeTo(null);
            setSwitching(null);
          }}
          onConfirm={async () => {
            await changeTier(downgradeTo);
            setDowngradeTo(null);
          }}
          currentTier={current}
          targetTier={downgradeTo}
          memberCount={memberCount}
          chordUploadCount={chordCount}
          periodEnd={sub?.currentPeriodEnd}
          busy={!!switching}
        />
      )}
      <CancelPlanModal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelError(null);
        }}
        onConfirm={cancel}
        currentTier={current}
        memberCount={memberCount}
        periodEnd={sub?.currentPeriodEnd}
        busy={cancelBusy}
        error={cancelError}
      />
    </div>
  );
};

function TierCard({
  tier,
  current,
  currentTier,
  founder,
  switching,
  onChoose,
}: {
  tier: PricingTier;
  current: boolean;
  currentTier: string;
  founder: boolean;
  switching: PaidTier | null;
  onChoose: (t: PaidTier) => void;
}) {
  const enterprise = tier.tier === 'enterprise';
  const founderPrice = founder && tier.price > 0 && !enterprise;
  const price = founderPrice ? Math.round(tier.price * 50) / 100 : tier.price;
  const features = getTierFeatures(tier.tier);
  const upgrade = RANK[tier.tier] > RANK[currentTier as keyof typeof RANK];
  const members = tier.maxMembers >= 999999 ? 'Unlimited members' : `Up to ${tier.maxMembers} members`;
  const teams = tier.maxTeams >= 999999 ? 'unlimited teams' : `${tier.maxTeams} team${tier.maxTeams === 1 ? '' : 's'}`;

  return (
    <div className={cx('rounded-[24px] border-[1.5px] bg-card p-6 flex flex-col', current ? 'border-acc' : 'border-line')}>
      <div className="flex justify-between items-center gap-2">
        <h2 className="text-[17px] font-semibold">{tier.name}</h2>
        {current ? (
          <StatusPill tone="acc">Current</StatusPill>
        ) : features.audioSeparation ? (
          <StatusPill tone="amb" className="!text-[11px] !tracking-[.06em]">
            SONG STUDIO + STEMS
          </StatusPill>
        ) : features.chordAi ? (
          <StatusPill tone="amb" className="!text-[11px] !tracking-[.06em]">
            SONG STUDIO
          </StatusPill>
        ) : null}
      </div>
      <div className="text-[13.5px] text-t4 mt-1">
        {members} · {teams}
      </div>
      <div className="flex items-baseline gap-1.5 my-5">
        {enterprise ? (
          <span className="font-mono text-[36px] font-bold">Custom</span>
        ) : (
          <>
            <span className="font-mono text-[36px] font-bold">{price === 0 ? '$0' : `$${price}`}</span>
            <span className="text-[13px] text-t4">/month</span>
            {founderPrice && <span className="font-mono text-[14px] text-t5 line-through ml-1">${tier.price}</span>}
          </>
        )}
      </div>
      <ul className="flex flex-col gap-2.5 flex-1 mb-[22px]">
        {tier.features.map((f) => (
          <li key={f} className="flex gap-2.5 text-[14px] text-t2">
            <span className="text-ok font-bold" aria-hidden="true">
              ✓
            </span>
            {f}
          </li>
        ))}
      </ul>
      {current ? (
        <Button variant="ghost" block disabled className="!h-11">
          Current plan
        </Button>
      ) : tier.tier === 'free' ? null : enterprise ? (
        <Button variant="dark" block className="!h-11" onClick={() => onChoose('enterprise')}>
          Contact sales
        </Button>
      ) : (
        <Button
          variant={upgrade ? 'primary' : 'ghost'}
          block
          className="!h-11"
          loading={switching === tier.tier}
          disabled={!!switching}
          onClick={() => onChoose(tier.tier as PaidTier)}
        >
          {upgrade ? `Upgrade to ${tier.displayName}` : `Switch to ${tier.displayName}`}
        </Button>
      )}
    </div>
  );
}
